import { inject, Injectable, Injector } from '@angular/core';
import { BehaviorSubject, tap } from 'rxjs';
import { TuiDialogService } from '@taiga-ui/core';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { TranslateService } from '@ngx-translate/core';
import { isEqual } from 'lodash-es';
import { UserProfileService } from '@src/api';
import { CitySwitcherService } from '@src/app/modules/search-cities/city-switcher';

import { CategoriesDialogComponent, CityDialogComponent } from '../dialogs';
import { SessionService } from '../../auth';
import { InputItemType } from '../../search-cities';

@Injectable({
  providedIn: 'root',
})
export class LpUserProfileService {
  private readonly userProfileService = inject(UserProfileService);
  private readonly dialogService = inject(TuiDialogService);
  private readonly injector = inject(Injector);
  private readonly translateService = inject(TranslateService);
  private readonly citySwitcherService = inject(CitySwitcherService);
  private readonly sessionService = inject(SessionService);

  userCityId$ = new BehaviorSubject<number | null>(null);
  favoriteCategories$ = new BehaviorSubject<string[] | null>(null);

  readonly authUser$ = this.sessionService.authUser$;

  readonly cityDialog = this.dialogService.open(new PolymorpheusComponent(CityDialogComponent, this.injector), {
    label: this.translateService.instant('components.loyaltyProgram.dialogs.cityDialogHeader'),
    closeable: false,
    dismissible: false,
    size: 'm',
    required: true,
  });

  constructor() {
    this.citySwitcherService.currentCity$.subscribe(city => {
      if (city?.cityId && this.authUser$.value?.cityId !== city.cityId) {
        this.updateUserCity(city).subscribe();
      }
    });

    this.authUser$.subscribe(authUser => {
      if (!authUser) return;

      const { cityId, favoriteCategories } = authUser;

      if (cityId && cityId !== this.userCityId$.value) {
        this.userCityId$.next(cityId);
      }

      if (favoriteCategories && !isEqual(favoriteCategories, this.favoriteCategories$.value)) {
        this.favoriteCategories$.next(favoriteCategories);
      }
    });
  }

  showFavoriteCategoriesDialog(selectedCategoriesIds?: string[]) {
    return this.dialogService.open<string[] | undefined>(
      new PolymorpheusComponent(CategoriesDialogComponent, this.injector),
      {
        closeable: false,
        dismissible: false,
        size: 'm',
        data: selectedCategoriesIds,
      },
    );
  }

  updateUserCity(city: InputItemType) {
    return this.userProfileService.updateUserCity({ cityId: city.cityId }).pipe(
      tap(() => {
        this.sessionService.updateAuthUser({ city, cityId: city.cityId });
      }),
    );
  }

  updateUserFavoriteCategories(categoriesIds: string[]) {
    return this.userProfileService.updateUserFavoriteCategories(categoriesIds).pipe(
      tap(() => {
        this.sessionService.updateAuthUser({ favoriteCategories: categoriesIds });
      }),
    );
  }
}
